import React from 'react';

import { AgenticCheckoutValue, ConfigResponse } from '../../store/modules/merchant/types';
import PageWrapper from '../../common/PageWrapper';
import { useMerchant } from '../../contexts/MerchantContext';
import { AgenticCheckoutForm } from './components/AgenticCheckoutForm';
import { toast } from '../../util/toast';
import {
  useGetCallbacksQuery,
  useGetConfigsQuery,
  useDeleteCallbackMutation,
} from '../../store/modules/merchant/api';
import { useConfigUpsert } from '../../hooks';
import { useCallbackUpsert } from '../../hooks/useCallbackUpsert';
import { isCallbackInheritedForMerchant } from '../../util/configInheritance';

export const AgenticCheckout = () => {
  const { merchantId, isLoading: merchantInfoLoading, canUpdateMerchantSettings } = useMerchant();
  const hasModifyAccess = canUpdateMerchantSettings();
  const {
    data: configs,
    isLoading: isLoadingConfigs,
    refetch: refetchConfigs,
  } = useGetConfigsQuery(merchantId, {
    skip: !merchantId,
  });

  const {
    data: callbacks,
    isLoading: isLoadingCallbacks,
    refetch: refetchCallbacks,
  } = useGetCallbacksQuery(merchantId, {
    skip: !merchantId,
  });

  const agenticCheckoutConfig = configs?.find((c) => c.name === 'AGENTIC_CHECKOUT') as
    ConfigResponse<AgenticCheckoutValue> | undefined;
  const validateTotalCallback = callbacks?.find(
    (cb) => cb.callbackType === 'AgenticCheckoutValidateTotal',
  );

  const { onConfigUpsert, isLoading: isSaving } = useConfigUpsert(merchantId);
  const { onCallbackUpsert, isLoading: isSavingCallbacks } = useCallbackUpsert(merchantId);
  const [deleteCallback] = useDeleteCallbackMutation();

  return (
    <PageWrapper
      showParentHierarchyBanner
      title="Agentic Checkout"
      description="Manage the Agentic Checkout configuration for this merchant. These settings control transaction limits and API call restrictions for the Skipify MCP."
      isLoading={merchantInfoLoading || isLoadingConfigs || isLoadingCallbacks}
      data-testid="AGENTIC_CHECKOUT"
    >
      <AgenticCheckoutForm
        key={`${merchantId}-${agenticCheckoutConfig?.updatedAt ?? 'no-config'}-${
          validateTotalCallback?.callbackId ?? 'no-callback'
        }`}
        agenticCheckoutConfig={agenticCheckoutConfig}
        callbacks={callbacks}
        onSave={async ({ config, callback, shouldSaveConfig, shouldSaveCallback }) => {
          const validateTotalCallbackRow = callbacks?.find(
            (cb) => cb.callbackType === 'AgenticCheckoutValidateTotal',
          );
          const callbackId = validateTotalCallbackRow?.callbackId;
          const validateTotalCallbackInherited = isCallbackInheritedForMerchant(
            merchantId,
            validateTotalCallbackRow,
            { missingIsInherited: false },
          );
          const promises: Promise<unknown>[] = [];
          if (shouldSaveConfig) {
            promises.push(
              onConfigUpsert({
                ...agenticCheckoutConfig,
                enabled: true,
                name: 'AGENTIC_CHECKOUT',
                value: config,
              }),
            );
          }
          if (shouldSaveCallback && !validateTotalCallbackInherited) {
            if (callback.isEnabled) {
              const validateTotalUrl = callback.validateTotalCallbackUrl?.trim() ?? '';
              if (!validateTotalUrl) {
                toast.error('A callback URL is required when validate total callback is enabled.');
                return false;
              }
              promises.push(
                onCallbackUpsert({
                  ...validateTotalCallbackRow,
                  callbackType: 'AgenticCheckoutValidateTotal',
                  url: validateTotalUrl,
                }),
              );
            } else if (callbackId) {
              promises.push(
                deleteCallback({
                  callbackId,
                  merchantId,
                }),
              );
            }
          }

          if (promises.length === 0) {
            return false;
          }

          try {
            await Promise.all(promises);
            const refetchTasks: Promise<unknown>[] = [];
            if (shouldSaveCallback) {
              refetchTasks.push(refetchCallbacks());
            }
            if (shouldSaveConfig) {
              refetchTasks.push(refetchConfigs());
            }
            await Promise.all(refetchTasks);
            toast.success('Changes saved successfully');
            return true;
          } catch {
            toast.error(
              'An error occurred while saving these changes. Please try again later or contact Skipify Support',
            );
            return false;
          }
        }}
        isSaving={isSaving || isSavingCallbacks}
        readOnly={!hasModifyAccess}
      />
    </PageWrapper>
  );
};
